import {
	Box,
	Button,
	FormControl,
	FormErrorMessage,
	Heading,
	Icon,
	Image,
	SimpleGrid,
	Stack,
	Text,
	Textarea,
	Input,
	VStack
} from "@chakra-ui/react";
import { FaEnvelope } from "react-icons/fa";
import FullScreenSection from "./FullScreenSection";
import { useState } from "react";
import { useToast } from "@chakra-ui/react";

export default function Influencer() {	
	const [values, setValues] = useState({
		fullName: "",
		email: "",
		phone: "",
		instagram: "",
		twitter: "",
		tiktok: "",
		youtube: "",
		comments: ""
	});
	const [touched, setTouched] = useState({
		fullName: false,
		email: false,
		phone: false
	});
	const [loading, setLoading] = useState(false);
	const toast = useToast();
	const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8001";

	const emailIsValid = /^\S+@\S+\.\S+$/.test(values.email);
	const fullNameError = touched.fullName && values.fullName.trim() === "";
	const emailRequiredError = touched.email && values.email.trim() === "";
	const emailFormatError = touched.email && values.email.trim() !== "" && !emailIsValid;
	const phoneError = touched.phone && values.phone.trim() === "";

	const handleChange = (field) => (event) => {
		setValues((prev) => ({ ...prev, [field]: event.target.value }));
	};

	const handleBlur = (field) => () => {
		setTouched((prev) => ({ ...prev, [field]: true }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		if (fullNameError || emailRequiredError || emailFormatError || phoneError) {
			toast({
				title: "Please fill all required fields correctly.",
				status: "warning",
				duration: 3000,
				isClosable: true,
			});
			return;
		}
		setLoading(true);
		try {
			const response = await fetch(`${API_BASE_URL}/api/influencer/`, {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
					"X-Client-App": "swiftcredit",
				},
				body: JSON.stringify(values),
			});
			const data = await response.json();
			if (response.ok && data.status === "success") {
				toast({
					title: "Application submitted!",
					description: "Thank you for applying. We'll be in touch soon.",
					status: "success",
					duration: 4000,
					isClosable: true,
				});
				setValues({
					fullName: "",
					email: "",
					phone: "",
					instagram: "",
					twitter: "",
					tiktok: "",
					youtube: "",
					comments: ""
				});
				setTouched({ fullName: false, email: false, phone: false });
			} else {
				toast({
					title: "Submission failed",
					description: data.message || "An error occurred.",
					status: "error",
					duration: 4000,
					isClosable: true,
				});
			}
		} catch (error) {
			toast({
				title: "Network error",
				description: "Could not connect to server.",
				status: "error",
				duration: 4000,
				isClosable: true,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<VStack spacing={0} width="100%">
			<FullScreenSection
				justifyContent="center"
				alignItems="center"
				isDarkBackground={false}
				backgroundColor="gray.50"
				py={100}
			>
				<VStack spacing={8} maxW="1000px" textAlign="center">
					<Heading fontSize={{ base: "2.2rem", md: "3rem" }} fontWeight="bold">
						Why Partner with Swift Credit as a Creator?
					</Heading>
					<Text px = {{ base: "4", md: "4" }} fontSize="1.15rem" color="gray.600" lineHeight="1.8">
						Join our creator community to help more Canadians learn about fair, fast, and transparent borrowing.
						We collaborate with influencers to create authentic content around financial awareness{/*, payday loans,*/}
						and short-term lending solutions that support real people.
					</Text>

					
					<Button
						as="a"
						href="#creator-form"
						size="lg"
						bg="black"
						color="white"
						borderRadius="full"
						px={10}
						_hover={{ bg: "gray.800", transform: "translateY(-2px)" }}
					>
						Apply as a creator
					</Button>
				</VStack>
			</FullScreenSection>

			<FullScreenSection
				justifyContent="center"
				alignItems="center"
				isDarkBackground={false}
				backgroundColor="white"
				py={20}
			>
				<VStack spacing={10} width="100%" maxW="1200px">
					<VStack spacing={3} textAlign="center" maxW="800px">
						<Heading fontSize={{ base: "1.6rem", md: "2rem" }} fontWeight="bold">
							What you can create with us
						</Heading>
						<Text color="gray.600" lineHeight="1.8">
							Educational videos, testimonials, and social campaigns that build trust and drive meaningful impact.
						</Text>
					</VStack>
					<SimpleGrid columns={{ base: 1, sm: 2, md: 3 }} spacing={6} width="100%">
						<Image
							src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=600&h=600&fit=crop"
							alt="Creator collaboration"
							borderRadius="xl"
							objectFit="cover"
							boxShadow="xl"
						/>
						<Image
							src="https://images.unsplash.com/photo-1521737604893-d14cc237f11d?w=600&h=600&fit=crop"
							alt="Content creation"
							borderRadius="xl"
							objectFit="cover"
							boxShadow="xl"
						/>
						<Image
							src="https://images.unsplash.com/photo-1522075469751-3a6694fb2f61?w=600&h=600&fit=crop"
							alt="Influencer team"
							borderRadius="xl"
							objectFit="cover"
							boxShadow="xl"
						/>
					</SimpleGrid>
				</VStack>
			</FullScreenSection>

			<FullScreenSection
				justifyContent="center"
				alignItems="center"
				isDarkBackground={false}
				backgroundColor="gray.50"
				py={100}
			>
				<VStack py={24} id="creator-form" spacing={8} maxW="900px" width="100%" align="center">
					<Heading fontSize={{ base: "2rem", md: "2.6rem" }} textAlign="center">
						Creator Partnership Application
					</Heading>

					<form style={{ width: "100%" }} onSubmit={handleSubmit}>
					<Stack spacing={5} width={{ base: "85%", md: "100%" }} mx="auto">
						<FormControl  isInvalid={fullNameError}>
							<Input
								placeholder="Full name"
								size="lg"
								bg="white"
								value={values.fullName}
								onChange={handleChange("fullName")}
								onBlur={handleBlur("fullName")}
								required
							/>
							<FormErrorMessage>This field is required</FormErrorMessage>
						</FormControl>
						<FormControl isInvalid={emailRequiredError || emailFormatError}>
							<Input
								placeholder="Email"
								type="email"
								size="lg"
								bg="white"
								value={values.email}
								onChange={handleChange("email")}
								onBlur={handleBlur("email")}
								required
							/>
							<FormErrorMessage>
								{emailRequiredError ? "This field is required" : "Enter a valid email"}
							</FormErrorMessage>
						</FormControl>
						<FormControl isInvalid={phoneError}>
							<Input
								placeholder="Phone number"
								type="tel"
								size="lg"
								bg="white"
								value={values.phone}
								onChange={handleChange("phone")}
								onBlur={handleBlur("phone")}
								required
							/>
							<FormErrorMessage>This field is required</FormErrorMessage>
						</FormControl>
						<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
							<Input placeholder="Instagram" size="lg" bg="white" value={values.instagram} onChange={handleChange("instagram")} />
							<Input placeholder="Twitter" size="lg" bg="white" value={values.twitter} onChange={handleChange("twitter")} />
							<Input placeholder="TikTok" size="lg" bg="white" value={values.tiktok} onChange={handleChange("tiktok")} />
							<Input placeholder="YouTube" size="lg" bg="white" value={values.youtube} onChange={handleChange("youtube")} />
						</SimpleGrid>
						<Textarea
							placeholder="Additional comments"
							size="lg"
							bg="white"
							minH="140px"
							value={values.comments}
							onChange={handleChange("comments")}
						/>

						<Button size="lg" bg="black" color="white" borderRadius="full" _hover={{ bg: "gray.800" }} type="submit" isLoading={loading}>
							Submit application
						</Button>

						<VStack spacing={2} align="center" pt={2}>
							<Icon as={FaEnvelope} boxSize={6} color="gray.600" />
							<Text fontSize="1rem" color="gray.600" textAlign="center">
								You can also contact us by email at partners@swiftchang.com.
							</Text>
                            {/*<Text fontSize="1rem" color="black.600" textAlign="center">
								partners@swiftchang.com
							</Text>*/}
						</VStack>
					</Stack>
					</form>
				</VStack>
			</FullScreenSection>
			
			
		</VStack>
	);
}
